import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ContratoFormManual from "../components/contratos/ContratoFormManual";
import ContratoUploadIA from "../components/contratos/ContratoUploadIA";

export default function CriarContratoPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [metodo, setMetodo] = useState("manual");
  const [saveDebug, setSaveDebug] = useState({
    startedAt: null,
    payloadKeys: [],
  });

  const { data: editais = [] } = useQuery({
    queryKey: ["editais"],
    queryFn: () => entities.Edital.list(),
  });

  const criarContratoMutation = useMutation({
    mutationFn: (data) => entities.Contrato.create(data),
    retry: 0, // evitar múltiplas tentativas em caso de falha (ex.: timeout)
    onMutate: (payload) => {
      const keys = payload
        ? Object.keys(payload).filter((k) => payload[k] !== null && payload[k] !== undefined)
        : [];
      setSaveDebug({ startedAt: Date.now(), payloadKeys: keys });
      console.log("[DEBUG] salvar contrato (IA) iniciado:", {
        startedAt: new Date().toISOString(),
        payloadKeys: keys,
      });
    },
    onSuccess: (contrato) => {
      toast.success("Contrato criado com sucesso!");
      console.log("[DEBUG] salvar contrato (IA) sucesso:", {
        contratoId: contrato?.id,
        contratoNumero: contrato?.numeroContrato ?? contrato?.numero_contrato,
      });
      navigate(createPageUrl(`VisualizarContrato?id=${contrato.id}`));
    },
    onError: (error, variables) => {
      console.error("[DEBUG] Erro ao criar contrato:", error);
      try {
        console.error("[DEBUG] Payload enviado (variables):", JSON.stringify(variables, null, 2));
      } catch {
        console.error("[DEBUG] Payload enviado (variables) (não serializável)");
      }
      console.error("[DEBUG] error.status:", error?.status, "error.message:", error?.message);
      toast.error(getDisplayMessage(error, "Erro ao criar contrato. Tente novamente."));
    },
    onSettled: (data, error) => {
      console.log("[DEBUG] salvar contrato (IA) settled:", {
        endedAt: new Date().toISOString(),
        ok: !!data && !error,
        errorStatus: error?.status,
      });
      setSaveDebug((prev) => ({ ...prev, startedAt: null }));
    },
  });

  const handleSalvarManual = async (dados) => {
    criarContratoMutation.mutate({
      ...dados,
      status: "Rascunho",
      historico_status: [
        {
          status: "Rascunho",
          data: new Date().toISOString(),
          usuario: user?.email ?? '',
          observacao: "Contrato criado manualmente",
        },
      ],
    });
  };

  const handleSalvarIA = async (dados) => {
    // O backend de `Contratos.create` só utiliza um subconjunto de campos.
    // Para evitar travar/parsing com payload gigante do LLM (arrays/objetos extras),
    // enviamos apenas o que a API realmente persiste.
    const payload = {
      edital_id: dados.edital_id ?? dados.editalId ?? null,
      numero_contrato: dados.numero_contrato ?? null,
      orgao_contratante: dados.orgao_contratante ?? null,
      fornecedor: dados.fornecedor ?? null,
      objeto: dados.objeto ?? null,
      status: "Em Validação",
      data_inicio: dados.data_inicio ?? dados.dataInicio ?? null,
      data_fim: dados.data_fim ?? dados.dataFim ?? null,
      valor_total: dados.valor_total ?? dados.valor ?? null,
      valor_mensal_previsto: dados.valor_mensal_previsto ?? dados.valorMensalPrevisto ?? null,
      alertas_vencimento: dados.alertas_vencimento ?? dados.alertasVencimento ?? null,
      periodicidade_reajuste: dados.periodicidade_reajuste ?? dados.periodicidadeReajuste ?? null,
      forma_pagamento: dados.forma_pagamento ?? dados.formaPagamento ?? null,
      centro_custo: dados.centro_custo ?? dados.centroCusto ?? null,
      fonte_recurso: dados.fonte_recurso ?? dados.fonteRecurso ?? null,
      classificacao_orcamentaria: dados.classificacao_orcamentaria ?? dados.classificacaoOrcamentaria ?? null,
      numero_processo: dados.numero_processo ?? dados.numeroProcesso ?? null,
      modalidade: dados.modalidade ?? null,
      tipo_contrato: dados.tipo_contrato ?? dados.tipoContrato ?? null,
      unidade_gestora: dados.unidade_gestora ?? dados.unidadeGestora ?? null,
      cnpj_cpf: dados.cnpj_cpf ?? dados.cnpjCpf ?? null,
      gestor_contrato: dados.gestor_contrato ?? dados.gestorContrato ?? null,
      responsavel_interno: dados.responsavel_interno ?? dados.responsavelInterno ?? null,
      multas_penalidades: dados.multas_penalidades ?? dados.multasPenalidades ?? null,
      clausulas_principais: dados.clausulas_principais ?? dados.clausulasPrincipais ?? null,
      arquivo_contrato_url: dados.arquivo_contrato_url ?? dados.arquivoContratoUrl ?? null,
      arquivo_contrato_nome: dados.arquivo_contrato_nome ?? dados.arquivoContratoNome ?? null,
      anexos: Array.isArray(dados.anexos) ? dados.anexos : null,
    };

    console.log(
      "[DEBUG] handleSalvarIA - payload para API (campos essenciais):",
      JSON.stringify(
        { numero_contrato: payload.numero_contrato, valor_total: payload.valor_total, data_inicio: payload.data_inicio, data_fim: payload.data_fim },
        null,
        2
      )
    );

    criarContratoMutation.mutate(payload);
  };

  // Failsafe: se por algum motivo a promise não voltar ao React Query (pending eterno),
  // removemos o loading e mostramos um erro com info pra depuração.
  useEffect(() => {
    if (!criarContratoMutation.isPending) return;
    if (!saveDebug.startedAt) return;
    const elapsedMs = Date.now() - saveDebug.startedAt;
    const timeoutMs = 120_000;
    if (elapsedMs > timeoutMs) {
      toast.error("Salvar demorou demais. Veja o console para o debug.");
      console.error("[DEBUG] salvar contrato (IA) pendente > 120s:", {
        elapsedMs,
        payloadKeys: saveDebug.payloadKeys,
      });
      criarContratoMutation.reset();
      setSaveDebug({ startedAt: null, payloadKeys: [] });
    }
  }, [criarContratoMutation.isPending, saveDebug.startedAt, saveDebug.payloadKeys, criarContratoMutation]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(createPageUrl("Contratos"))}
              className="mb-4 -ml-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              Novo Contrato
            </h1>
            <p className="text-slate-600 mt-1">
              Crie um novo contrato manualmente ou usando IA
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <Tabs value={metodo} onValueChange={setMetodo} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual" className="gap-2">
                  <FileText className="w-4 h-4" />
                  Cadastro Manual
                </TabsTrigger>
                <TabsTrigger value="ia" className="gap-2">
                  <Upload className="w-4 h-4" />
                  Upload com IA
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>

          <CardContent>
            {metodo === "manual" ? (
              <ContratoFormManual
                onSalvar={handleSalvarManual}
                editais={editais}
                isLoading={criarContratoMutation.isPending}
              />
            ) : (
              <ContratoUploadIA
                onSalvar={handleSalvarIA}
                editais={editais}
                isLoading={criarContratoMutation.isPending}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}